import { useState, useEffect } from "react";
import stationHeroImg from "../../../assets/images/station-hero.png";
import stationBgImg from "../../../assets/images/station-bg.png";

interface PhoneMockupProps {
  theme?: "dark" | "light";
  activeScreen?: number;
}

export default function PhoneMockup({ theme = "dark", activeScreen }: PhoneMockupProps) {
  const [screen, setScreen] = useState(0);
  const [fading, setFading] = useState(false);

  const currentScreen = activeScreen !== undefined ? activeScreen : screen;

  useEffect(() => {
    if (activeScreen !== undefined) return;
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setScreen(s => (s + 1) % 3);
        setFading(false);
      }, 500);
    }, 4000);
    return () => clearInterval(interval);
  }, [activeScreen]);

  useEffect(() => {
    if (activeScreen !== undefined) {
      setFading(true);
      setTimeout(() => setFading(false), 300);
    }
  }, [activeScreen]);

  return (
    <div className="relative flex items-center justify-center">
      {/* Phone outer frame */}
      <div
        style={{
          width: 290,
          height: 630,
          background: "#000000",
          borderRadius: 50,
          padding: 3,
          boxShadow: "0 40px 80px rgba(0,122,255,0.15), 0 20px 40px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.08)",
          position: "relative",
          flexShrink: 0,
        }}
      >
        {/* Inner screen area */}
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: 47,
            overflow: "hidden",
            position: "relative",
            background: "#0A0A10",
          }}
        >
          {/* Dynamic Island — sits at very top of frame, above status bar */}
          <div
            style={{
              position: "absolute",
              top: 12,
              left: "50%",
              transform: "translateX(-50%)",
              width: 120,
              height: 35,
              background: "#000000",
              borderRadius: 20,
              zIndex: 30,
              pointerEvents: "none",
            }}
          />

          {/* Screen content with fade */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              opacity: fading ? 0 : 1,
              transition: "opacity 0.4s ease",
            }}
          >
            {currentScreen === 0 && <MapScreen />}
            {currentScreen === 1 && <StationScreen />}
            {currentScreen === 2 && <UpdateScreen />}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── SHARED STATUS BAR ──────────────────────────────────── */
function StatusBar({ transparent = false }: { transparent?: boolean }) {
  return (
    <div
      style={{
        height: 54,
        flexShrink: 0,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        paddingLeft: 22,
        paddingRight: 22,
        paddingBottom: 8,
        background: transparent ? "transparent" : "transparent",
        position: "relative",
        zIndex: 10,
      }}
    >
      <span
        style={{
          color: "white",
          fontSize: 15,
          fontWeight: 600,
          fontFamily: "-apple-system, system-ui, sans-serif",
          lineHeight: 1,
        }}
      >
        04:37
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <i className="ri-signal-wifi-fill" style={{ color: "white", fontSize: 13 }} />
        <i className="ri-battery-fill" style={{ color: "white", fontSize: 13 }} />
      </div>
    </div>
  );
}

/* ─── SHARED TAB BAR ─────────────────────────────────────── */
function TabBar({ active = 0 }: { active?: number }) {
  const tabs = [
    { icon: "ri-map-2-fill", label: "Map" },
    { icon: "ri-list-unordered", label: "Regions" },
    { icon: "ri-notification-3-line", label: "Alerts" },
    { icon: "ri-user-line", label: "Profile" },
  ];

  return (
    <div
      style={{
        background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(20px)",
        borderTop: "1px solid rgba(255,255,255,0.10)",
        height: 82,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", flex: 1, paddingTop: 8 }}>
        {tabs.map((tab, i) => (
          <div key={tab.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <i
              className={tab.icon}
              style={{
                fontSize: 20,
                color: i === active ? "#007AFF" : "rgba(255,255,255,0.40)",
              }}
            />
            <span
              style={{
                fontSize: 9,
                fontWeight: i === active ? 600 : 400,
                color: i === active ? "#007AFF" : "rgba(255,255,255,0.40)",
                fontFamily: "-apple-system, system-ui, sans-serif",
              }}
            >
              {tab.label}
            </span>
          </div>
        ))}
      </div>
      {/* Home indicator */}
      <div style={{ display: "flex", justifyContent: "center", paddingBottom: 8 }}>
        <div style={{ width: 134, height: 5, background: "rgba(255,255,255,0.30)", borderRadius: 3 }} />
      </div>
    </div>
  );
}

/* ─── SCREEN 1: MAP ──────────────────────────────────────── */
function MapScreen() {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#0A0A10",
      }}
    >
      {/* Status bar — 54px, content below dynamic island */}
      <StatusBar />

      {/* Search bar */}
      <div style={{ padding: "0 12px 8px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 12px",
              borderRadius: 12,
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <i className="ri-search-line" style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }} />
            <span style={{ color: "rgba(255,255,255,0.40)", fontSize: 11, fontFamily: "-apple-system, system-ui, sans-serif" }}>
              Search fuel stations...
            </span>
          </div>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.08)",
              flexShrink: 0,
            }}
          >
            <i className="ri-equalizer-line" style={{ color: "white", fontSize: 14 }} />
          </div>
        </div>
      </div>

      {/* Filter chips */}
      <div style={{ display: "flex", gap: 6, padding: "0 12px 10px", overflow: "hidden", flexShrink: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "4px 10px",
            borderRadius: 20,
            background: "rgba(0,122,255,0.20)",
            border: "1px solid rgba(0,122,255,0.40)",
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#007AFF" }} />
          <span style={{ color: "#007AFF", fontSize: 9, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif" }}>
            Your location · 0
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "4px 10px",
            borderRadius: 20,
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.10)",
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(255,255,255,0.40)" }} />
          <span style={{ color: "rgba(255,255,255,0.60)", fontSize: 9, fontFamily: "-apple-system, system-ui, sans-serif" }}>
            Hodh ech Chargui
          </span>
        </div>
        <div
          style={{
            padding: "4px 10px",
            borderRadius: 20,
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.10)",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: "rgba(255,255,255,0.60)", fontSize: 9, fontFamily: "-apple-system, system-ui, sans-serif" }}>Trarza</span>
        </div>
      </div>

      {/* Map area — fills remaining space */}
      <div style={{ flex: 1, position: "relative", background: "#0A0A10", overflow: "hidden" }}>
        {/* Road grid SVG */}
        <svg
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          viewBox="0 0 284 320"
          preserveAspectRatio="xMidYMid slice"
        >
          <g stroke="rgba(255,255,255,0.09)" strokeWidth="1.2" fill="none">
            <line x1="0" y1="70" x2="284" y2="90" />
            <line x1="0" y1="130" x2="284" y2="150" />
            <line x1="0" y1="190" x2="284" y2="210" />
            <line x1="0" y1="250" x2="284" y2="270" />
            <line x1="55" y1="0" x2="75" y2="320" />
            <line x1="120" y1="0" x2="140" y2="320" />
            <line x1="185" y1="0" x2="205" y2="320" />
            <line x1="240" y1="0" x2="260" y2="320" />
          </g>
          <g stroke="rgba(255,255,255,0.04)" strokeWidth="0.8" fill="none">
            <line x1="0" y1="40" x2="284" y2="55" />
            <line x1="0" y1="100" x2="284" y2="115" />
            <line x1="0" y1="160" x2="284" y2="175" />
            <line x1="0" y1="220" x2="284" y2="235" />
            <line x1="88" y1="0" x2="100" y2="320" />
            <line x1="155" y1="0" x2="167" y2="320" />
            <line x1="220" y1="0" x2="232" y2="320" />
          </g>
          <g stroke="rgba(255,255,255,0.035)" strokeWidth="0.6" fill="rgba(255,255,255,0.015)">
            <rect x="20" y="20" width="38" height="28" rx="2" />
            <rect x="80" y="35" width="48" height="38" rx="2" />
            <rect x="155" y="15" width="55" height="45" rx="2" />
            <rect x="15" y="100" width="42" height="32" rx="2" />
            <rect x="82" y="108" width="52" height="42" rx="2" />
            <rect x="152" y="95" width="65" height="50" rx="2" />
            <rect x="20" y="175" width="40" height="30" rx="2" />
            <rect x="85" y="180" width="50" height="38" rx="2" />
            <rect x="155" y="170" width="60" height="45" rx="2" />
          </g>
        </svg>

        {/* City label */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <span
            style={{
              color: "rgba(255,255,255,0.18)",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 3,
              fontFamily: "-apple-system, system-ui, sans-serif",
            }}
          >
            Nouakchott
          </span>
        </div>

        {/* User location — pulsing blue dot */}
        <div style={{ position: "absolute", left: "40%", top: "38%" }}>
          <div style={{ position: "relative", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div
              style={{
                position: "absolute",
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "rgba(0,122,255,0.35)",
                animation: "ping 1.5s cubic-bezier(0,0,0.2,1) infinite",
              }}
            />
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "#007AFF",
                border: "2.5px solid white",
                boxShadow: "0 0 10px rgba(0,122,255,0.6)",
                zIndex: 1,
              }}
            />
          </div>
        </div>

        {/* Red marker — unavailable */}
        <div style={{ position: "absolute", left: "58%", top: "55%" }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: "#FF3B30",
              border: "2px solid rgba(255,255,255,0.80)",
              boxShadow: "0 0 8px rgba(255,59,48,0.50)",
            }}
          />
        </div>

        {/* Blue marker — available */}
        <div style={{ position: "absolute", left: "52%", top: "68%" }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: "#007AFF",
              border: "2px solid rgba(255,255,255,0.80)",
              boxShadow: "0 0 8px rgba(0,122,255,0.50)",
            }}
          />
        </div>
      </div>

      <TabBar active={0} />
    </div>
  );
}

/* ─── SCREEN 2: STATION DETAIL ───────────────────────────── */
function StationScreen() {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#1C1C1E",
        overflow: "hidden",
      }}
    >
      {/* Hero image with status bar overlaid */}
      <div style={{ position: "relative", height: 180, flexShrink: 0 }}>
        <img
          src={stationHeroImg}
          alt="BPM Station"
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
        />
        {/* Dark gradient overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.20) 50%, rgba(0,0,0,0.60) 100%)",
          }}
        />

        {/* Status bar — transparent, overlaid on hero */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 54,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            paddingLeft: 22,
            paddingRight: 22,
            paddingBottom: 8,
            zIndex: 10,
          }}
        >
          <span
            style={{
              color: "white",
              fontSize: 15,
              fontWeight: 600,
              fontFamily: "-apple-system, system-ui, sans-serif",
              lineHeight: 1,
            }}
          >
            04:37
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <i className="ri-signal-wifi-fill" style={{ color: "white", fontSize: 13 }} />
            <i className="ri-battery-fill" style={{ color: "white", fontSize: 13 }} />
          </div>
        </div>

        {/* Back button — below status bar */}
        <div
          style={{
            position: "absolute",
            top: 58,
            left: 12,
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <i className="ri-arrow-left-s-line" style={{ color: "white", fontSize: 16 }} />
        </div>

        {/* Rating badge */}
        <div
          style={{
            position: "absolute",
            bottom: 10,
            left: 12,
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            borderRadius: 20,
            background: "rgba(0,0,0,0.60)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.15)",
            zIndex: 10,
          }}
        >
          <i className="ri-star-fill" style={{ color: "#FF9500", fontSize: 10 }} />
          <span style={{ color: "white", fontSize: 10, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif" }}>4.8</span>
        </div>
      </div>

      {/* Content — fixed layout, no scroll, no tab bar */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: "10px 12px 12px",
          gap: 7,
          overflow: "hidden",
        }}
      >
        {/* Station name */}
        <div>
          <p style={{ color: "white", fontWeight: 700, fontSize: 15, fontFamily: "-apple-system, system-ui, sans-serif", margin: 0 }}>BPM Station</p>
          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 10, fontFamily: "-apple-system, system-ui, sans-serif", margin: "2px 0 0" }}>74e Carffor jambor</p>
        </div>

        {/* 3 stat cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
          {[
            { icon: "ri-shopping-cart-line", value: "24/7", label: "Services" },
            { icon: "ri-time-line", value: "5 min", label: "Wait" },
            { icon: "ri-map-pin-line", value: "6.6 km", label: "Distance" },
          ].map(s => (
            <div
              key={s.label}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                padding: "8px 4px",
                borderRadius: 10,
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <i className={s.icon} style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }} />
              <span style={{ color: "white", fontSize: 10, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif" }}>{s.value}</span>
              <span style={{ color: "rgba(255,255,255,0.40)", fontSize: 8, fontFamily: "-apple-system, system-ui, sans-serif" }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Open badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 12px",
            borderRadius: 10,
            background: "rgba(52,199,89,0.12)",
            border: "1px solid rgba(52,199,89,0.25)",
          }}
        >
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#34C759" }} />
          <span style={{ color: "#34C759", fontSize: 10, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif" }}>Open 24/7</span>
        </div>

        {/* Fuel types label */}
        <p style={{ color: "rgba(255,255,255,0.40)", fontSize: 8, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", fontFamily: "-apple-system, system-ui, sans-serif", margin: 0 }}>
          Available Fuel Types
        </p>

        {/* 2 fuel cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {[
            { type: "Gasoline", price: "50.13" },
            { type: "Diesel", price: "50.12" },
          ].map(f => (
            <div
              key={f.type}
              style={{
                padding: "8px 10px",
                borderRadius: 10,
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 3, marginBottom: 4 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#34C759" }} />
                <span style={{ color: "#34C759", fontSize: 8, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif" }}>Available</span>
              </div>
              <div style={{ fontSize: 14 }}>⛽</div>
              <p style={{ color: "white", fontSize: 10, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif", margin: "3px 0 1px" }}>{f.type}</p>
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 9, fontFamily: "-apple-system, system-ui, sans-serif", margin: 0 }}>{f.price} MRU/L</p>
            </div>
          ))}
        </div>

        {/* EV card */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "7px 10px",
            borderRadius: 10,
            background: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13 }}>⚡</span>
            <div>
              <p style={{ color: "white", fontSize: 10, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif", margin: 0 }}>EV Charging</p>
              <p style={{ color: "rgba(255,255,255,0.40)", fontSize: 8, fontFamily: "-apple-system, system-ui, sans-serif", margin: "1px 0 0" }}>4 fast chargers</p>
            </div>
          </div>
          <div
            style={{
              padding: "2px 7px",
              borderRadius: 20,
              background: "rgba(255,149,0,0.15)",
              border: "1px solid rgba(255,149,0,0.30)",
            }}
          >
            <span style={{ color: "#FF9500", fontSize: 8, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif" }}>Low charge</span>
          </div>
        </div>

        {/* Update button — at very bottom, no tab bar */}
        <button
          style={{
            width: "100%",
            padding: "10px 0",
            borderRadius: 20,
            background: "#007AFF",
            color: "white",
            fontSize: 11,
            fontWeight: 600,
            fontFamily: "-apple-system, system-ui, sans-serif",
            border: "none",
            cursor: "pointer",
            flexShrink: 0,
            marginTop: "auto",
          }}
        >
          Update Fuel Status
        </button>
      </div>
      {/* NO tab bar, NO home indicator */}
    </div>
  );
}

/* ─── SCREEN 3: UPDATE MODAL ─────────────────────────────── */
function UpdateScreen() {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#1C1C1E",
        overflow: "hidden",
      }}
    >
      {/* Top 35% — blurred station detail visible behind sheet */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <img
          src={stationBgImg}
          alt="bg"
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", opacity: 0.30, filter: "blur(3px)" }}
        />
        {/* Dark overlay */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} />

        {/* Status bar — transparent, overlaid on blurred bg */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 54,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            paddingLeft: 22,
            paddingRight: 22,
            paddingBottom: 8,
            zIndex: 10,
          }}
        >
          <span
            style={{
              color: "white",
              fontSize: 15,
              fontWeight: 600,
              fontFamily: "-apple-system, system-ui, sans-serif",
              lineHeight: 1,
            }}
          >
            04:37
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <i className="ri-signal-wifi-fill" style={{ color: "white", fontSize: 13 }} />
            <i className="ri-battery-fill" style={{ color: "white", fontSize: 13 }} />
          </div>
        </div>
      </div>

      {/* Bottom sheet — takes bottom 65% */}
      <div
        style={{
          borderRadius: "28px 28px 0 0",
          padding: "12px 14px 16px",
          background: "rgba(22,22,26,0.97)",
          backdropFilter: "blur(30px)",
          borderTop: "1px solid rgba(255,255,255,0.10)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          flexShrink: 0,
          height: "65%",
          overflow: "hidden",
        }}
      >
        {/* Drag handle */}
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 2 }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.25)" }} />
        </div>

        <div>
          <p style={{ color: "white", fontWeight: 700, fontSize: 16, fontFamily: "-apple-system, system-ui, sans-serif", margin: 0 }}>Update fuel status</p>
          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, fontFamily: "-apple-system, system-ui, sans-serif", margin: "4px 0 0" }}>
            You can update Diesel and/or Gasoline.
          </p>
        </div>

        {/* Diesel card — active / toggle ON */}
        <div
          style={{
            padding: "10px 12px",
            borderRadius: 14,
            background: "rgba(0,122,255,0.10)",
            border: "1px solid rgba(0,122,255,0.25)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13 }}>⛽</span>
              <span style={{ color: "white", fontSize: 12, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif" }}>Diesel</span>
            </div>
            {/* Toggle ON */}
            <div style={{ width: 38, height: 22, borderRadius: 11, background: "#007AFF", display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "0 3px" }}>
              <div style={{ width: 16, height: 16, borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
            </div>
          </div>
          {/* Status pills */}
          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            {[
              { label: "● Available", active: true },
              { label: "● Low stock", active: false },
              { label: "● Unavailable", active: false },
            ].map(p => (
              <div
                key={p.label}
                style={{
                  padding: "3px 6px",
                  borderRadius: 20,
                  background: p.active ? "rgba(52,199,89,0.20)" : "rgba(255,255,255,0.07)",
                  border: p.active ? "1px solid rgba(52,199,89,0.40)" : "1px solid rgba(255,255,255,0.10)",
                  color: p.active ? "#34C759" : "rgba(255,255,255,0.45)",
                  fontSize: 8,
                  fontWeight: 600,
                  fontFamily: "-apple-system, system-ui, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                {p.label}
              </div>
            ))}
          </div>
          {/* Price input */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderRadius: 8,
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <span style={{ color: "rgba(255,255,255,0.40)", fontSize: 9, fontFamily: "-apple-system, system-ui, sans-serif" }}>Price</span>
            <span style={{ color: "white", fontSize: 10, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif", flex: 1 }}>50.1 MRU/L</span>
          </div>
          <p style={{ color: "rgba(255,255,255,0.28)", fontSize: 8, fontFamily: "-apple-system, system-ui, sans-serif", margin: "4px 0 0" }}>Max ceiling: 500 MRU/L</p>
        </div>

        {/* Gasoline card — inactive / toggle OFF */}
        <div
          style={{
            padding: "10px 12px",
            borderRadius: 14,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13 }}>⛽</span>
            <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, fontWeight: 600, fontFamily: "-apple-system, system-ui, sans-serif" }}>Gasoline</span>
          </div>
          {/* Toggle OFF */}
          <div style={{ width: 38, height: 22, borderRadius: 11, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "flex-start", padding: "0 3px" }}>
            <div style={{ width: 16, height: 16, borderRadius: "50%", background: "rgba(255,255,255,0.60)", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
          </div>
        </div>

        {/* Confirm button */}
        <button
          style={{
            width: "100%",
            padding: "11px 0",
            borderRadius: 20,
            background: "#007AFF",
            color: "white",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: "-apple-system, system-ui, sans-serif",
            border: "none",
            cursor: "pointer",
            flexShrink: 0,
            marginTop: "auto",
          }}
        >
          Confirm update
        </button>
      </div>
      {/* NO tab bar, NO home indicator */}
    </div>
  );
}
